import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import type { CurrentUser } from '../../types/user';
import type { Campaign } from '../../types/campaign';
import type {
  AccountCampaign,
  AccountCampaignBulkAction,
} from '../../types/accountCampaign';
import { resolveBulkRefreshTarget } from './bulkRefreshGate';
import { resolveBulkPreviewTarget } from './bulkPreviewGate';
import { resolveBulkSendTarget } from './bulkSendGate';

// Bulk-action bar (spec §7.1). Visible when one or more rows are selected.
// Each action gathers its argument(s) inline and emits a dispatch to the
// parent, which posts to /api/rollouts/:id/rows/bulk and renders the
// partial-success toast from the response.

type Props = {
  selectedIds: string[];
  onClear: () => void;
  onDispatch: (action: AccountCampaignBulkAction) => void;
  users: CurrentUser[];
  campaigns: Campaign[];
  // Multi Lead Form spec §4. Bulk-assign Lead Form needs to know which
  // Campaign(s) the selected rows belong to so the dropdown can show the
  // right entries and so cross-Campaign selection is detected up-front
  // (spec §4.1 option A).
  rows: AccountCampaign[];
  // Granular Refresh spec §1. Fired with the single shared Campaign when
  // the gated "Refresh from Campaign…" button is clicked. Optional so the
  // gating/tooltip can land before the modal is wired (commit ordering).
  onRefreshFromCampaign?: (campaign: Campaign) => void;
  // Ad Preview Mockups Stage 1 §3.2. Fires when the gated "Generate
  // Previews…" button is clicked. Parent opens the BulkPreviewModal with
  // the current selection — it already knows the row ids + Account names
  // and doesn't need them passed back.
  onGeneratePreviews?: () => void;
  // Ad Preview Stage 3 §3.2. Fires when the gated "Send Previews…"
  // button is clicked. Parent opens the BulkSendPreviewModal with the
  // eligible selection.
  onSendPreviews?: () => void;
  disabled: boolean; // e.g. rollout archived
};

function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function BulkActionBar({
  selectedIds,
  onClear,
  onDispatch,
  users,
  campaigns,
  rows,
  onRefreshFromCampaign,
  onGeneratePreviews,
  onSendPreviews,
  disabled,
}: Props) {
  const [mode, setMode] = useState<
    | null
    | 'assignAm'
    | 'assignCampaign'
    | 'setStartDate'
    | 'setEndDate'
    | 'assignLeadForm'
  >(null);
  const [pickedAm, setPickedAm] = useState<string>('');
  const [pickedCampaign, setPickedCampaign] = useState<string>('');
  const [pickedStart, setPickedStart] = useState<string>('');
  const [pickedEnd, setPickedEnd] = useState<string>('');
  const [pickedLeadForm, setPickedLeadForm] = useState<string>('');

  const count = selectedIds.length;

  // Multi Lead Form spec §4.1 option A. Detect cross-Campaign selection
  // up-front so the "Assign Lead Form…" affordance disables itself instead
  // of opening a modal the AM then has to dismiss. Filters out unassigned /
  // opt-out rows since they have no Campaign to bulk-assign against.
  const leadFormContext = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    const eligible = rows.filter(
      (r) => selectedSet.has(r.id) && r.rowType === 'campaign' && r.campaignId !== null,
    );
    const campaignIds = new Set(eligible.map((r) => r.campaignId as string));
    if (eligible.length === 0) {
      return {
        sharedCampaign: null,
        reason: 'No selected rows have an assigned Campaign.' as string | null,
      };
    }
    if (campaignIds.size > 1) {
      return {
        sharedCampaign: null,
        reason:
          'Select rows from a single Campaign for bulk lead form assignment.' as string | null,
      };
    }
    const [campaignId] = [...campaignIds];
    const c = campaigns.find((cp) => cp.id === campaignId) ?? null;
    if (!c) {
      return {
        sharedCampaign: null,
        reason: "Selected rows' Campaign is not available." as string | null,
      };
    }
    return { sharedCampaign: c, reason: null as string | null };
  }, [campaigns, rows, selectedIds]);

  // Granular Refresh spec §1. Single-Campaign gate for the bulk refresh
  // button. Disabled (with an explanatory tooltip) on multi-Campaign,
  // no-Campaign, or mixed selections.
  const refreshGate = useMemo(
    () => resolveBulkRefreshTarget(selectedIds, rows, campaigns),
    [selectedIds, rows, campaigns],
  );
  // Stage 1 §13.13. Bulk Preview no longer requires a single shared
  // Campaign — each preview is per-row independent. The dedicated
  // gate enforces per-row eligibility only (campaign rowType +
  // campaignId + not soft-deleted).
  const previewGate = useMemo(
    () => resolveBulkPreviewTarget(selectedIds, rows),
    [selectedIds, rows],
  );

  // Ad Preview Stage 3 §3.2. Bulk Send requires per-row eligibility
  // PLUS a generated preview link to send. The server re-checks link
  // validity per row; the modal shows the aggregate recipient count.
  const sendGate = useMemo(
    () => resolveBulkSendTarget(selectedIds, rows),
    [selectedIds, rows],
  );

  if (count === 0) return null;

  return (
    <div className="sticky top-2 z-20 mb-3 rounded-md border border-slate-300 bg-white shadow-lg p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-800">{count} selected</span>
        <span className="text-slate-300">|</span>

        <Button
          variant="outline"
          disabled={disabled}
          onClick={() =>
            onDispatch({ kind: 'setMetaReady', accountCampaignIds: selectedIds, value: true })
          }
        >
          Mark Meta Ready
        </Button>
        <Button
          variant="outline"
          disabled={disabled}
          onClick={() =>
            onDispatch({ kind: 'setMetaReady', accountCampaignIds: selectedIds, value: false })
          }
        >
          Clear Meta Ready
        </Button>
        <Button
          variant="outline"
          disabled={disabled}
          onClick={() =>
            onDispatch({ kind: 'setGoogleReady', accountCampaignIds: selectedIds, value: true })
          }
        >
          Mark Google Ready
        </Button>
        <Button
          variant="outline"
          disabled={disabled}
          onClick={() =>
            onDispatch({ kind: 'setGoogleReady', accountCampaignIds: selectedIds, value: false })
          }
        >
          Clear Google Ready
        </Button>

        <Button variant="outline" disabled={disabled} onClick={() => setMode('assignAm')}>
          Assign AM…
        </Button>
        <Button variant="outline" disabled={disabled} onClick={() => setMode('assignCampaign')}>
          Assign Campaign…
        </Button>
        <Button variant="outline" disabled={disabled} onClick={() => setMode('setStartDate')}>
          Set Start Date…
        </Button>
        <Button variant="outline" disabled={disabled} onClick={() => setMode('setEndDate')}>
          Set End Date…
        </Button>
        {/* Multi Lead Form spec §4. Disabled when the selection spans
            multiple Campaigns or has no Campaign rows (leadFormContext
            populates `reason`); the title attribute carries the
            explanation so AMs hover-discover the constraint. */}
        <Button
          variant="outline"
          disabled={disabled || leadFormContext.sharedCampaign === null}
          onClick={() => setMode('assignLeadForm')}
          title={leadFormContext.reason ?? undefined}
        >
          Assign Lead Form…
        </Button>
        {/* Granular Refresh spec §1. Disabled when the selection spans
            multiple Campaigns / has no Campaign rows; the title carries
            the reason so AMs hover-discover the constraint. */}
        <Button
          variant="outline"
          disabled={disabled || refreshGate.target === null}
          onClick={() =>
            refreshGate.target && onRefreshFromCampaign?.(refreshGate.target)
          }
          title={refreshGate.reason ?? undefined}
        >
          Refresh from Campaign…
        </Button>
        {/* Stage 1 §13.13. Bulk Preview only requires per-row
            eligibility (campaign rowType + campaignId + not deleted).
            Mixed-Campaign selections are admitted — each preview
            is per-row independent. Single-Campaign constraint
            removed from the tooltip text. */}
        <Button
          variant="outline"
          disabled={disabled || previewGate.eligibleIds.length === 0}
          onClick={() => onGeneratePreviews?.()}
          title={previewGate.reason ?? undefined}
        >
          Generate Previews…
        </Button>
        {/* Ad Preview Stage 3 §3.2. Send Previews -- gated on rows
            having a generated preview link to send. Aggregates per
            contact in the modal. */}
        <Button
          variant="outline"
          disabled={disabled || sendGate.eligibleIds.length === 0}
          onClick={() => onSendPreviews?.()}
          title={sendGate.reason ?? undefined}
        >
          Send Previews…
        </Button>

        <button type="button" onClick={onClear} className="ml-auto text-sm text-slate-500 hover:text-slate-800 underline">
          Clear selection
        </button>
      </div>

      {mode === 'assignAm' && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <select
            value={pickedAm}
            onChange={(e) => setPickedAm(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">— Unassign —</option>
            {users.map((u) => (
              <option key={u.uid} value={u.uid}>
                {formatUserDisplayName(u)}
              </option>
            ))}
          </select>
          <Button
            onClick={() => {
              onDispatch({
                kind: 'assignAm',
                accountCampaignIds: selectedIds,
                assignedAmId: pickedAm || null,
              });
              setMode(null);
              setPickedAm('');
            }}
          >
            Apply
          </Button>
          <Button variant="outline" onClick={() => setMode(null)}>Cancel</Button>
        </div>
      )}

      {mode === 'assignCampaign' && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <select
            value={pickedCampaign}
            onChange={(e) => setPickedCampaign(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">— Unassign (rows go to 'unassigned') —</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.isAlternate ? '(Alternate)' : c.isOptOut ? '(Opt Out)' : ''}
              </option>
            ))}
          </select>
          <Button
            onClick={() => {
              if (!window.confirm(`Assign Campaign to ${count} rows? Any prior content snapshot on those rows will be replaced.`)) return;
              onDispatch({
                kind: 'assignCampaign',
                accountCampaignIds: selectedIds,
                campaignId: pickedCampaign || null,
              });
              setMode(null);
              setPickedCampaign('');
            }}
          >
            Apply
          </Button>
          <Button variant="outline" onClick={() => setMode(null)}>Cancel</Button>
        </div>
      )}

      {mode === 'setStartDate' && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <input
            type="datetime-local"
            value={pickedStart}
            onChange={(e) => setPickedStart(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
          <Button
            onClick={() => {
              onDispatch({
                kind: 'setStartDate',
                accountCampaignIds: selectedIds,
                startDate: localToIso(pickedStart),
              });
              setMode(null);
              setPickedStart('');
            }}
          >
            Apply
          </Button>
          <Button variant="outline" onClick={() => setMode(null)}>Cancel</Button>
        </div>
      )}

      {mode === 'setEndDate' && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <input
            type="datetime-local"
            value={pickedEnd}
            onChange={(e) => setPickedEnd(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
          <Button
            onClick={() => {
              onDispatch({
                kind: 'setEndDate',
                accountCampaignIds: selectedIds,
                endDate: localToIso(pickedEnd),
              });
              setMode(null);
              setPickedEnd('');
            }}
          >
            Apply
          </Button>
          <Button variant="outline" onClick={() => setMode(null)}>Cancel</Button>
        </div>
      )}

      {/* Multi Lead Form spec §4. Dropdown of the shared Campaign's
          lead form entries; default entry surfaces a "default" badge
          inline so the AM can see it without needing a separate column.
          Cross-Campaign / no-Campaign cases are unreachable here
          because the "Assign Lead Form…" button is disabled upstream
          when leadFormContext.reason is set. */}
      {mode === 'assignLeadForm' && leadFormContext.sharedCampaign && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <span className="text-xs text-slate-500">
            Assigning across {count} rows on Campaign “{leadFormContext.sharedCampaign.name}”:
          </span>
          <select
            value={pickedLeadForm}
            onChange={(e) => setPickedLeadForm(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">— Pick a lead form —</option>
            {leadFormContext.sharedCampaign.campaignLeadForms.map((lf) => (
              <option key={lf.id} value={lf.id}>
                {lf.name}
                {lf.id === leadFormContext.sharedCampaign!.defaultLeadFormId ? ' (default)' : ''}
              </option>
            ))}
          </select>
          <Button
            disabled={!pickedLeadForm}
            onClick={() => {
              if (!pickedLeadForm) return;
              onDispatch({
                kind: 'assignLeadForm',
                accountCampaignIds: selectedIds,
                leadFormId: pickedLeadForm,
              });
              setMode(null);
              setPickedLeadForm('');
            }}
          >
            Apply
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setMode(null);
              setPickedLeadForm('');
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
